import { useLocation, useNavigate, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Eyebrow, Reveal } from "../lib/ui";

const PAGES = {
  privatlivspolitik: {
    title: "Privatlivspolitik",
    intro:
      "Denne privatlivspolitik beskriver, hvordan Juris ApS behandler personoplysninger, når du bruger hjemmesiden og vores kontaktformular.",
    sections: [
      [
        "Dataansvarlig",
        "Juris ApS, CVR 45141233, Odeons Kvarter 10, 1, 5000 Odense C, er dataansvarlig for behandlingen af de personoplysninger, du afgiver via hjemmesiden.\n\nHar du spørgsmål til vores behandling af dine oplysninger, kan du altid skrive til kontakt@juris.dk.",
      ],
      [
        "Hvilke oplysninger behandler vi",
        "Når du bruger kontaktwizarden (\"Fortæl os om din sag\"), modtager vi de oplysninger, du selv afgiver:\n\n• Navn og telefonnummer\n• E-mailadresse\n• Om du henvender dig som privatperson eller virksomhed\n• Sagsområde og den konkrete ydelse\n• Din beskrivelse af sagen\n• Eventuelle vedhæftede dokumenter\n• Ønsket kontaktmetode (e-mail eller opkald)\n\nVi behandler ikke andre oplysninger end dem, du selv afgiver gennem formularen eller via e-mail.",
      ],
      [
        "Formål og retsgrundlag",
        "Oplysningerne behandles udelukkende for at besvare din henvendelse, vurdere din sag og aftale et eventuelt samarbejde. Behandlingen har retsgrundlag i databeskyttelsesforordningens art. 6, stk. 1, litra b (forudgående kontraktopfyldelse) og litra f (vores berettigede interesse i at kunne behandle og besvare henvendelser).\n\nIndtil et samarbejde er aftalt, opstår der ikke et kundeforhold eller en mandatkontrakt, og oplysningerne behandles derfor ikke som kundeoplysninger i den forstand. Behandler vi oplysninger om din sag efter et aftalt samarbejde, gælder vores tavshedspligt og de særskilte vilkår for samarbejdet.",
      ],
      [
        "Vedhæftede dokumenter",
        "Dokumenter, du vedhæfter i kontaktwizarden, kan indeholde følsomme oplysninger — fx om helbred, økonomi eller familieforhold. Vi behandler dokumenterne fortroligt og udelukkende med det formål at vurdere din sag.\n\nVi anbefaler, at du kun vedhæfter de dokumenter, der er nødvendige for, at vi kan forstå sagen, og at du kun vedhæfter dokumenter, du selv har ret til at videregive.",
      ],
      [
        "Hvor længe opbevarer vi oplysningerne",
        "Henvendelser, der ikke fører til et samarbejde, slettes senest 12 måneder efter din sidste henvendelse, medmindre vi er forpligtede til at opbevare oplysningerne længere — fx efter bogføringsloven, hvis der er ydet rådgivning mod betaling.",
      ],
      [
        "Deling med tredjepart",
        "Dine oplysninger videregives ikke til tredjepart med henblik på markedsføring. Vi benytter en leverandør til afsendelse og opbevaring af e-mail (databehandler), som kun behandler oplysningerne efter vores instruks.\n\nVideregiver vi oplysninger i øvrigt, sker det kun, når det er nødvendigt for at varetage din sag, eller når der foreligger en juridisk forpligtelse.",
      ],
      [
        "Sikkerhed",
        "Vi har truffet passende tekniske og organisatoriske foranstaltninger mod, at oplysningerne kommer fremmede til gode. Kun medarbejdere, der skal bruge oplysningerne for at kunne besvare din henvendelse, har adgang til dem, og alle medarbejdere er underlagt tavshedspligt.",
      ],
      [
        "Dine rettigheder",
        "Du har efter databeskyttelsesreglerne en række rettigheder i forhold til vores behandling af dine oplysninger:\n\n• Ret til indsigt\n• Ret til berigtigelse\n• Ret til sletning\n• Ret til begrænsning af behandlingen\n• Ret til dataportering\n• Ret til at gøre indsigelse\n\nDu kan gøre brug af dine rettigheder ved at skrive til kontakt@juris.dk. Vi besvarer henvendelsen hurtigst muligt og senest én måned efter modtagelsen.\n\nHvis du mener, at vores behandling ikke er i overensstemmelse med reglerne, kan du klage til Datatilsynet via datatilsynet.dk.",
      ],
      [
        "Cookies",
        "Vi bruger kun cookies og lignende teknologier, som beskrevet i vores cookiepolitik. Der sættes ikke statistik- eller marketingcookies, før du selv giver samtykke.",
      ],
      [
        "Ændringer af politikken",
        "Vi opdaterer politikken, hvis vores behandling af personoplysninger ændres. Den gældende version er altid tilgængelig her på hjemmesiden.",
      ],
    ],
  },
  cookiepolitik: {
    title: "Cookiepolitik",
    intro:
      "Denne cookiepolitik beskriver, hvordan og hvorfor Juris ApS bruger cookies og lignende teknologier på hjemmesiden.",
    sections: [
      [
        "Hvad er cookies",
        "Cookies er små tekstfiler, der gemmes i din browser, når du besøger en hjemmeside. De kan bruges til at få siden til at fungere, huske dine præferencer eller måle, hvordan siden bruges.",
      ],
      [
        "Samtykke og dit valg",
        "Ved dit første besøg viser vi et cookie-banner, hvor du vælger, hvilke cookies du vil tillade. Du giver kun samtykke til de kategorier, du selv vælger — vi sætter ikke statistik- eller marketingcookies, før du aktivt siger ja.\n\nVælger du \"Kun nødvendige\", begrænser vi os til de teknisk nødvendige funktioner, og du kan altid omvendt vælge at tage statistik-cookies ind senere.",
      ],
      [
        "Kategorier af cookies",
        "• Nødvendige: Dit cookievalg gemmes i din browsers lokale lager under \"juris_cookie_consent\", så vi ikke spørger igen ved hvert besøg. Oplysningen gemmes kun hos dig og udelukkende for at respektere dit valg. Opbevaringsperiode: 12 måneder.\n\n• Statistik: Aktiveres kun, hvis du selv vælger det i banneret. Vi bruger dem til at måle, hvordan hjemmesiden bruges, med anonymiserede data, så vi kan forbedre indholdet.\n\n• Marketing: Vi bruger ikke marketingcookies i dag. Kategorien findes, så du kan styre den, hvis vi senere får brug for den.",
      ],
      [
        "Tredjepartscookies",
        "Hjemmesiden sætter i dag ikke cookies fra tredjeparter — hverken fra sociale medier, reklamenetværk eller analysetjenester.",
      ],
      [
        "Sådan ændrer du dit valg",
        "Du kan altid trække dit samtykke tilbage eller ændre dit valg ved at klikke på \"Cookieindstillinger\" nederst på siden — så åbner banneret igen, og du kan justere din indstilling.\n\nØnsker du at slette dit samtykke helt, kan du gøre det i din browsers indstillinger under sletning af lagerdata for denne side. Så spørger vi igen ved dit næste besøg.",
      ],
      [
        "Ændringer og kontakt",
        "Ændrer vi i vores brug af cookies, opdaterer vi denne politik, og du får mulighed for at træffe et nyt valg. Har du spørgsmål, er du velkommen til at kontakte os på kontakt@juris.dk.",
      ],
    ],
  },
  vilkaar: {
    title: "Vilkår",
    intro:
      "Disse vilkår gælder for brugen af hjemmesiden og for henvendelser via kontaktformularen til Juris ApS.",
    sections: [
      [
        "Brug af hjemmesiden",
        "Indholdet på denne hjemmeside er alene til generel information. Det udgør ikke juridisk rådgivning og må ikke anvendes som grundlag for beslutninger uden kompetent rådgivning i den konkrete situation.",
      ],
      [
        "Ingen rådgivning uden aftale",
        "En henvendelse via kontaktwizarden eller e-mail skaber ikke i sig selv et kundeforhold eller en mandatkontrakt. Først når der er aftalt et konkret samarbejde — herunder opgavens omfang og honoraret — påtager vi os opgaven, og de særskilte vilkår for samarbejdet gælder herefter.",
      ],
      [
        "Fortrolighed ved henvendelser",
        "Vi behandler alle henvendelser fortroligt og udelukkende med det formål at kunne besvare og vurdere din sag. Vi forbeholder os retten til at afvise en opgave — fx hvis der foreligger en interessekonflikt, eller sagen ligger uden for vores fagområder.",
      ],
      [
        "Vedhæftede dokumenter",
        "Når du vedhæfter dokumenter i kontaktwizarden, garanterer du, at du har ret til at dele dem med os, og at de ikke indeholder materiale, som du ikke må videregive. Vi beder om kun at modtage de dokumenter, der er nødvendige for at vurdere sagen.",
      ],
      [
        "Honorar og betaling",
        "Ved aftalt rådgivning aftales honorar og opgavens omfang konkret, før opgaven påbegyndes. Honoraret bestemmes efter lov om advokatvirksomhed, medmindre der er aftalt et fast honorar eller en anden beregning. Ved henvendelser uden aftalt samarbejde opkræves intet honorar.",
      ],
      [
        "Ansvarsbegrænsning",
        "Hjemmesidens indhold stilles til rådighed \"som det er\". Vi påtager os intet ansvar for tab, omkostninger eller skader, der skyldes brug af indholdet uden rådgivning i den konkrete situation.",
      ],
      [
        "Immaterielle rettigheder",
        "Alt indhold på hjemmesiden — tekster, layout, grafik og logoer — tilhører Juris ApS eller vores licensgivere. Indholdet må ikke kopieres, distribueres eller anvendes uden skriftligt samtykke.",
      ],
      [
        "Klageadgang",
        "Rådgivning, der udgør advokatvirksomhed, er omfattet af lov om advokatvirksomhed og Advokatsamfundets tilsyn. Er du utilfreds med et samarbejde, kan du klage til Advokatnævnet — se mere på advokatnaevnet.dk.",
      ],
      [
        "Gældende lov og tvister",
        "Disse vilkår og enhver tvist i forbindelse hermed er underlagt dansk ret. Tvister forsøges løst i forlig. Findes der ikke en løsning, kan tvisten indbringes for de almindelige domstole.",
      ],
      [
        "Ændringer af vilkårene",
        "Vi kan til enhver tid opdatere vilkårene. Den gældende version er altid tilgængelig her på hjemmesiden, og væsentlige ændringer vil fremgå tydeligt af siden.",
      ],
    ],
  },
};

export const LegalPage = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const page = pathname.split("/").filter(Boolean)[0] || "";
  const content = PAGES[page];

  return (
    <main data-testid="legal-page" className="min-h-screen bg-slate-50/60">
      <header className="bg-brand-950 pb-14 pt-[120px] sm:pt-[140px]">
        <div className="mx-auto max-w-3xl px-6 sm:px-8">
          <Reveal>
            <Eyebrow dark>Juris ApS — CVR 45141233</Eyebrow>
            <h1
              data-testid="legal-title"
              className="mt-3 text-3xl font-semibold tracking-[-0.02em] text-white sm:text-5xl"
            >
              {content?.title || "Side"}
            </h1>
            <p className="mt-4 text-base leading-relaxed text-white/70">{content?.intro}</p>
            <p data-testid="legal-updated" className="mt-6 text-xs text-white/50">
              Sidst opdateret: 13. september 2026
            </p>
          </Reveal>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-6 py-14 sm:px-8 sm:py-16">
        <button
          data-testid="legal-back"
          onClick={() => navigate("/")}
          className="group inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
          Tilbage til forsiden
        </button>

        <Reveal delay={0.05}>
          {content ? (
            content.sections.map(([h, text], i) => (
              <div key={h} data-testid={`legal-section-${i}`} className="mt-10 first:mt-12">
                <h2 className="text-xl font-semibold text-slate-900">{h}</h2>
                {text.split("\n\n").map((para, j) => {
                  const lines = para.split("\n").filter((l) => l.trim() !== "");
                  if (lines.length && lines.every((l) => l.trim().startsWith("•"))) {
                    return (
                      <ul key={j} className="mt-3 space-y-1.5">
                        {lines.map((l, k) => (
                          <li key={k} className="flex gap-2 leading-relaxed text-slate-600">
                            <span className="shrink-0 text-brand-600">•</span>
                            <span>{l.trim().slice(1).trim()}</span>
                          </li>
                        ))}
                      </ul>
                    );
                  }
                  return (
                    <p key={j} className="mt-3 leading-relaxed text-slate-600">
                      {para}
                    </p>
                  );
                })}
              </div>
            ))
          ) : (
            <p className="mt-10 text-slate-600">Siden findes desværre ikke.</p>
          )}

          <div className="mt-14 rounded-2xl border border-slate-200 bg-white p-6">
            <p className="text-sm font-semibold text-slate-900">Har du spørgsmål?</p>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              Kontakt os på{" "}
              <a
                href="mailto:kontakt@juris.dk"
                data-testid="legal-email"
                className="font-medium text-brand-600 hover:underline"
              >
                kontakt@juris.dk
              </a>{" "}
              — eller brug{" "}
              <Link to="/kontakt" data-testid="legal-contact-link" className="font-medium text-brand-600 hover:underline">
                kontaktwizarden
              </Link>
              , så vender vi tilbage med næste skridt.
            </p>
            <p className="mt-3 text-xs text-slate-400">
              Juris ApS · CVR 45141233 · Odeons Kvarter 10, 1, 5000 Odense C
            </p>
          </div>
        </Reveal>
      </div>
    </main>
  );
};

export default LegalPage;
